import {
  FOC_TOUR_LABEL_FSLTP,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  IOC_CARRYOVER_DISPOSITION,
} from '../orgWorkspaceTour/orgWorkspaceTourSteps';
import {
  resolveSitlCopTourSeatHref,
  type SitlCopTourIncidentCandidate,
} from '../sitlCopTour/sitlCopTourSteps';
import { CorporateViewTabs } from '../hubNavTabs';

export const FOC_FSLTP_TOUR_PARAM = 'focFsltpTour';

/** Opens Hub Layers and starts the 5.6 FSLTP / ArcGIS walkthrough. */
export const FOC_FSLTP_TOUR_HREF = `/hub?openTab=Layers&${FOC_FSLTP_TOUR_PARAM}=1`;

export function focFsltpSitlPresentationHref(
  currentId: string | undefined,
  incidents: SitlCopTourIncidentCandidate[]
): string | null {
  const seat = resolveSitlCopTourSeatHref(currentId, incidents);
  if (!seat) return null;
  const url = new URL(seat, 'http://local.invalid');
  url.searchParams.delete('sitlCopTour');
  url.searchParams.set('openTab', CorporateViewTabs.Presentation);
  return `${url.pathname}${url.search}`;
}

export function focFsltpHubDailyHref(): string {
  return `/hub?openTab=${CorporateViewTabs.SitStat}`;
}

export function focFsltpLayersHref(): string {
  return `/hub?openTab=${CorporateViewTabs.Layers}`;
}

export type FocFsltpTourSurface = 'layers' | 'sitlPresentation' | 'hubDaily';

export type FocFsltpTourStepId =
  | 'sectionIntro'
  | 'fsltp001'
  | 'fsltp002'
  | 'fsltp003'
  | 'fsltp004'
  | 'fsltp005'
  | 'fsltp006'
  | 'cop010'
  | 'cop010a'
  | 'cop010b'
  | 'cop011'
  | 'cop012'
  | 'cop012a'
  | 'cop012b'
  | 'cop012c'
  | 'cop012d'
  | 'cop013'
  | 'cop013a'
  | 'cop013b'
  | 'focFeedback';

export type FocFsltpTourStepDef = {
  id: FocFsltpTourStepId;
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  bullets?: string[];
  tour?: string;
  surface?: FocFsltpTourSurface;
};

type RequirementInput = Omit<
  FocFsltpTourStepDef,
  'title' | 'requirementId'
> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): FocFsltpTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

/**
 * Walk for 5.6 FSLTP and ArcGIS Online Integration: US-FSLTP001–006,
 * FOC-COP-010–013, ending with FOC Feedback Notes.
 */
export const FOC_FSLTP_TOUR_STEPS: FocFsltpTourStepDef[] = [
  {
    id: 'sectionIntro',
    surface: 'layers',
    tour: 'hub-layers-workspace',
    title: '5.6 FSLTP and ArcGIS Online Integration',
    description:
      'Use public FSLTP and authenticated ArcGIS Online layers on the Hub COP, then connect enterprise GIS context to the Situation Unit Presentation (situation display) and daily-event awareness surfaces.',
  },
  requirement({
    id: 'fsltp001',
    requirementId: 'US-FSLTP001',
    name: 'Access Public FSLTP Layers',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-row',
    description:
      'View designated FSLTP (USCG ArcGIS Online) data layers on the PRATUS map without being required to log in or have an Esri account, so you have immediate access to common operational data (for example AIS, weather, and administrative boundaries).',
  }),
  requirement({
    id: 'fsltp002',
    requirementId: 'US-FSLTP002',
    name: 'View Layer Metadata',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-metadata',
    description:
      'Inspect attribute and provenance metadata for FSLTP and other map features (pop-ups / layer detail) so you can see specific details about the geographic features displayed.',
  }),
  requirement({
    id: 'fsltp003',
    requirementId: 'US-FSLTP003',
    name: 'Authenticate with ArcGIS Online',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-arcgis-connect',
    description:
      'With an existing ArcGIS account, authenticate to ArcGIS Online within PRATUS so the system recognizes your specific data access permissions.',
  }),
  requirement({
    id: 'fsltp004',
    requirementId: 'US-FSLTP004',
    name: 'Access Secured ArcGIS Content',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-arcgis-add',
    description:
      'As an authenticated ArcGIS user, add and view private or secured data layers your account can access, alongside public FSLTP layers.',
  }),
  requirement({
    id: 'fsltp005',
    requirementId: 'US-FSLTP005',
    name: 'Toggle Data Layers',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-row',
    description:
      'Toggle individual FSLTP/ArcGIS layers on and off in the Data Layers (Layers) menu so you can de-clutter the map and focus on relevant information.',
  }),
  requirement({
    id: 'fsltp006',
    requirementId: 'US-FSLTP006',
    name: 'View Layer Legends',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-legend',
    description:
      'See a legend for active FSLTP layers so you can interpret the symbology and color coding used on the map.',
  }),
  requirement({
    id: 'cop010',
    requirementId: 'FOC-COP-010',
    name: 'Enterprise GIS Context',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-layers-workspace',
    description:
      'PRATUS uses ArcGIS Online, SEXTANT, and FSLTP as the established enterprise GIS context for authorized COP capability development.',
  }),
  requirement({
    id: 'cop010a',
    requirementId: 'FOC-COP-010a',
    name: 'Direct and Indirect Linkage Transparency',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-metadata',
    description:
      'PRATUS and Appendix A identify whether an approved data product is provided through a direct PRATUS connection, SEXTANT, ArcGIS Online, FSLTP, manual import/export, link-out, or another approved relationship.',
  }),
  requirement({
    id: 'cop010b',
    requirementId: 'FOC-COP-010b',
    name: 'External Data Authorization',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-arcgis-connect',
    description:
      'Activation of an external system linkage remains subject to data-owner authorization, interface availability, C5I/security approval, and applicable data-sharing requirements.',
  }),
  requirement({
    id: 'cop011',
    requirementId: 'FOC-COP-011',
    name: 'COP Linkage Register',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    description:
      'Appendix A identifies required Hub data products and linkage candidates, including the operational question supported, data source, display/use need, user/echelon, cadence, relationship type, source metadata, permissions, external-user visibility, fallback behavior, dependency, and required government decision. (Register content is maintained outside this walkthrough.)',
  }),
  requirement({
    id: 'cop012',
    requirementId: 'FOC-COP-012',
    name: 'Situation Display / Briefing Board',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'sitlPresentation',
    tour: 'sitl-presentation-workspace',
    description:
      'PRATUS provides a Situation Unit Leader-managed Presentation that presents the most decision-relevant incident information in a compact, briefing-ready format and can be updated as conditions change without requiring the SITL to leave the briefing context for routine corrections.',
  }),
  requirement({
    id: 'cop012a',
    requirementId: 'FOC-COP-012a',
    name: 'Configurable Briefing Content',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'sitlPresentation',
    tour: 'presentation-slides',
    description:
      'Authorized SITL users can select, arrange, show, hide, and size approved display components such as current situation, objectives, safety message, map/SitStat views, resources, assets at risk, mission capability/readiness, incident metrics, weather/tides, facilities, operational status, and other incident-specific information as Presentation slides.',
  }),
  requirement({
    id: 'cop012b',
    requirementId: 'FOC-COP-012b',
    name: 'Direct Authorized Updates',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'sitlPresentation',
    tour: 'sitl-presentation-workspace',
    description:
      'Where PRATUS is the authoritative source for a displayed value, an authorized user can update the value from the situation-display workflow or navigate directly to the authoritative record. Where an external system is authoritative, the display identifies the source and does not permit an unsupported local edit to masquerade as authoritative data.',
  }),
  requirement({
    id: 'cop012c',
    requirementId: 'FOC-COP-012c',
    name: 'Resource and Partner Rollups',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'sitlPresentation',
    tour: 'sitl-presentation-workspace',
    description:
      'The display can summarize assigned resources by operationally meaningful categories, including Coast Guard component/status and participating federal, state, local, tribal, private, volunteer, or other partner resources when those data are available and authorized.',
  }),
  requirement({
    id: 'cop012d',
    requirementId: 'FOC-COP-012d',
    name: 'Briefing Product Identity',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'sitlPresentation',
    tour: 'sitl-presentation-workspace',
    description:
      'Saved or exported Presentations identify incident, operational period or as-of time, title, author or responsible unit, data/source timestamps where applicable, and publication/revision status via cover slides and deck metadata.',
  }),
  requirement({
    id: 'cop013',
    requirementId: 'FOC-COP-013',
    name: 'Daily-Event COP Data-Feed Dashboard',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'hubDaily',
    tour: 'sitstat-workspace',
    description:
      'PRATUS provides Hub SitStat and related data-feed surfaces capable of monitoring a real-time common operating picture for daily events, consistent with the contract software-development and integration requirement. Specific mission-area metrics, external data feeds, and authoritative-system interfaces remain subject to government prioritization, data-owner authorization, and security approval. (FOC Required — Contract-Directed Capability.)',
  }),
  requirement({
    id: 'cop013a',
    requirementId: 'FOC-COP-013a',
    name: 'Daily-to-Incident Escalation',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'hubDaily',
    tour: 'sitstat-workspace',
    description:
      'When a daily event becomes an incident requiring sustained incident management, authorized users can create or associate an incident workspace without re-entering information that PRATUS already holds and is authorized to reuse.',
  }),
  requirement({
    id: 'cop013b',
    requirementId: 'FOC-COP-013b',
    name: 'Scope Boundary',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'hubDaily',
    description:
      'Daily-event awareness does not by itself expand PRATUS into a replacement for unrelated Coast Guard command-and-control, readiness, logistics, or mission systems. New mission-specific workflows or external interfaces require the applicable government prioritization and approval.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_FSLTP),
  },
];

export function focFsltpTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focFsltpTourStepIds(): FocFsltpTourStepId[] {
  return FOC_FSLTP_TOUR_STEPS.map((step) => step.id);
}

export function focFsltpTourStepTitles(): string[] {
  return FOC_FSLTP_TOUR_STEPS.map((step) => step.title);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function focFsltpTourPopoverHtml(
  step: Pick<FocFsltpTourStepDef, 'disposition' | 'description' | 'bullets'>
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}
